import { Download } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

import { WithdrawalRequestStatus } from "Infrastructure/Api/Api";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { downloadInvoicePdf } from "Invoices/Utils/downloadInvoicePdf";
import { BackButton } from "Shared/Buttons/BackButton";
import { AuthenticatedLayout } from "Shared/Layouts/AuthenticatedLayout";
import { Price } from "Shared/Price/Price";
import { DateFormat, formatDate } from "Shared/Utils/Format/date";
import { useApproveWithdrawalMutation } from "WithdrawalApprovals/Api/Mutations/useApproveWithdrawalMutation";
import { useRejectWithdrawalMutation } from "WithdrawalApprovals/Api/Mutations/useRejectWithdrawalMutation";
import { useWithdrawalApprovalDetailQuery } from "WithdrawalApprovals/Api/Queries/useWithdrawalApprovalDetailQuery";
import { CzechQrCode } from "WithdrawalApprovals/Components/CzechQrCode";
import { EpcQrCode } from "WithdrawalApprovals/Components/EpcQrCode";
import { RejectWithdrawalDialog } from "WithdrawalApprovals/Components/RejectWithdrawalDialog";
import { WithdrawalStatusChip } from "WithdrawalApprovals/Components/WithdrawalStatusChip";

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

const DetailRow = ({ label, value }: DetailRowProps): React.ReactNode => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 1.5,
      borderBottom: "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value}
    </Typography>
  </Box>
);

export const WithdrawalApprovalDetailPage = (): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const { withdrawalRequestID } = useParams<{ withdrawalRequestID: string }>();

  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);

  const { data: detail, isLoading } =
    useWithdrawalApprovalDetailQuery(withdrawalRequestID);
  const approveMutation = useApproveWithdrawalMutation();
  const rejectMutation = useRejectWithdrawalMutation();

  const handleDownloadInvoice = useCallback(async () => {
    if (!detail?.invoiceID) return;
    setIsDownloadingInvoice(true);
    try {
      await downloadInvoicePdf(detail.invoiceID);
    } catch {
      toast.error(t(Resources.Invoices.DownloadError));
    } finally {
      setIsDownloadingInvoice(false);
    }
  }, [detail?.invoiceID, t]);

  const handleApprove = async () => {
    if (!withdrawalRequestID) return;

    try {
      await approveMutation.mutateAsync(withdrawalRequestID);
      toast.success(t(Resources.WithdrawalApprovals.ApproveSuccess));
    } catch {
      // auto-toast shown by hook
    }
  };

  const handleReject = async (reason: string) => {
    if (!withdrawalRequestID) return;

    try {
      await rejectMutation.mutateAsync({
        withdrawalRequestID,
        request: { reason },
      });
      toast.success(t(Resources.WithdrawalApprovals.RejectSuccess));
      setIsRejectDialogOpen(false);
    } catch {
      // auto-toast shown by hook
    }
  };

  if (isLoading) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.WithdrawalApprovals)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      </AuthenticatedLayout>
    );
  }

  if (!detail) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.WithdrawalApprovals)}>
        <Typography>{t(Resources.WithdrawalApprovals.NotFound)}</Typography>
      </AuthenticatedLayout>
    );
  }

  const isPending = detail.status === WithdrawalRequestStatus.Pending;
  const isRejected = detail.status === WithdrawalRequestStatus.Rejected;
  const isResolved =
    detail.status === WithdrawalRequestStatus.Approved ||
    detail.status === WithdrawalRequestStatus.Rejected;

  return (
    <AuthenticatedLayout
      preTitle={
        <BackButton
          defaultText={t(Resources.Common.BackToWithdrawalApprovals)}
          defaultPath={AppRouting.WithdrawalApprovals.path()}
        />
      }
      title={detail.actorName ?? "-"}
      action={
        <Stack direction="row" spacing={Spacing.S}>
          {detail.invoiceID && (
            <Button
              variant="outlined"
              startIcon={
                isDownloadingInvoice ? (
                  <CircularProgress size={18} />
                ) : (
                  <Download />
                )
              }
              onClick={handleDownloadInvoice}
              disabled={isDownloadingInvoice}
            >
              {t(Resources.Invoices.DownloadInvoice)}
            </Button>
          )}
          {isPending && (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setIsRejectDialogOpen(true)}
              >
                {t(Resources.WithdrawalApprovals.Reject)}
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleApprove}
                disabled={approveMutation.isPending}
              >
                {t(Resources.WithdrawalApprovals.Approve)}
              </Button>
            </>
          )}
        </Stack>
      }
    >
      <Grid container columnSpacing={Spacing.L} rowSpacing={Spacing.M}>
        <Grid size={6}>
          <Paper
            sx={{
              p: Spacing.L,
              backgroundColor: theme.colors.backgroundTile,
              borderRadius: Radius.M,
            }}
          >
            <DetailRow
              label={t(Resources.WithdrawalApprovals.ActorName)}
              value={detail.actorName ?? "-"}
            />
            <DetailRow
              label={t(Resources.WithdrawalApprovals.Amount)}
              value={<Price amount={detail.amount} />}
            />
            <DetailRow
              label={t(Resources.WithdrawalApprovals.IBAN)}
              value={detail.iban ?? "-"}
            />
            <DetailRow
              label={t(Resources.WithdrawalApprovals.SWIFT)}
              value={detail.swift ?? "-"}
            />
            <DetailRow
              label={t(Resources.WithdrawalApprovals.ActorBalance)}
              value={<Price amount={detail.actorBalance} />}
            />
            <DetailRow
              label={t(Resources.WithdrawalApprovals.Status)}
              value={<WithdrawalStatusChip status={detail.status} />}
            />
            <DetailRow
              label={t(Resources.WithdrawalApprovals.RequestedAt)}
              value={formatDate(detail.requestedAt, DateFormat.DateTime)}
            />
            <DetailRow
              label={t(Resources.WithdrawalApprovals.ExpiresAt)}
              value={formatDate(detail.expiresAt, DateFormat.DateTime)}
            />
            {isResolved && (
              <>
                <DetailRow
                  label={t(Resources.WithdrawalApprovals.ResolvedAt)}
                  value={formatDate(detail.resolvedAt, DateFormat.DateTime)}
                />
                <DetailRow
                  label={t(Resources.WithdrawalApprovals.ResolvedBy)}
                  value={detail.resolvedByUserName ?? "-"}
                />
              </>
            )}
            {isRejected && detail.rejectionReason && (
              <DetailRow
                label={t(Resources.WithdrawalApprovals.RejectionReason)}
                value={detail.rejectionReason}
              />
            )}
          </Paper>
        </Grid>
        <Grid size={6}>
          {isPending && detail.swift && detail.iban && detail.actorName && (
            <Paper
              sx={{
                p: Spacing.L,
                backgroundColor: theme.colors.backgroundTile,
                borderRadius: Radius.M,
              }}
            >
              <Stack direction="row" spacing={Spacing.L}>
                <EpcQrCode
                  swift={detail.swift}
                  actorName={detail.actorName}
                  iban={detail.iban}
                  amount={detail.amount}
                />
                <CzechQrCode
                  swift={detail.swift}
                  actorName={detail.actorName}
                  iban={detail.iban}
                  amount={detail.amount}
                />
              </Stack>
            </Paper>
          )}
        </Grid>
      </Grid>

      <RejectWithdrawalDialog
        isOpen={isRejectDialogOpen}
        isLoading={rejectMutation.isPending}
        onCancel={() => setIsRejectDialogOpen(false)}
        onConfirm={handleReject}
      />
    </AuthenticatedLayout>
  );
};
